import { Button, TextInput, type TextInputProps, useFieldController } from '@gitbook/spine-react';
import { normalizeURLSlug, validateURLSlug } from '@gitbook/util-url';

/**
 * Text input to edit a slug.
 *
 * Must be used inside a `TextField` with the validation function below.
 */
export function SlugInput(props: TextInputProps) {
    const { feedback: propsFeedback, onChange, ...rest } = props;

    const controller = useFieldController();
    if (!controller) {
        return <TextInput {...props} />;
    }

    const { field, fieldState } = controller;
    if (!fieldState.error) {
        return <TextInput {...props} />;
    }

    let normalizedSlug = normalizeURLSlug(field.value ?? '');
    const isSlugInvalid =
        typeof field.value === 'string' &&
        field.value !== 'undefined' &&
        normalizedSlug === 'undefined';
    // A non-page slug cannot be empty, if the normalizer returns "undefined"
    // but the field value isn't "undefined", it means the value didn't pass validation,
    // so we clear the input to trigger the field `required` validation if any.
    normalizedSlug = isSlugInvalid ? '' : normalizedSlug;

    return (
        <TextInput
            {...rest}
            value={field.value}
            onChange={(value) => {
                field.onChange(value);
            }}
            ref={field.ref}
            onBlur={field.onBlur}
            feedback={
                fieldState.error?.type === 'validate'
                    ? {
                          type: 'warning' as const,
                          message: (
                              <div className="flex items-start justify-between gap-2">
                                  <p className="mt-0.5">
                                      The slug contains unsupported characters or has an incorrect
                                      format for a URL.{' '}
                                      {normalizedSlug && (
                                          <>
                                              Update to:{' '}
                                              <span className="font-bold">{normalizedSlug}</span>
                                          </>
                                      )}
                                  </p>
                                  <Button
                                      label="Update"
                                      size="xsmall"
                                      kind="link"
                                      className="text-inherit"
                                      onPress={() => field.onChange(normalizedSlug)}
                                  />
                              </div>
                          ),
                      }
                    : { type: 'danger', message: fieldState.error?.message }
            }
        />
    );
}

/**
 * Validation to pass to the `validate` prop on the `TextField` parent of the `SlugInput`.
 */
export function validateSlugValue(value: string) {
    return value ? validateURLSlug(value) : true;
}
